import { getTime } from "@suse-o11y/date-time";
import { fromJSON } from "@suse-o11y/from-json";
import { isPresent } from "@suse-o11y/is-present";
import { LocalStorage } from "@suse-o11y/local-storage";
import { Signal } from "@suse-o11y/signal";
import first from "lodash/first";
import take from "lodash/take";
import uniqBy from "lodash/uniqBy";
import { useContext, useEffect, useMemo } from "react";
import { z } from "zod";

import { SpecificRange, type TimeRange } from "../../../app/state/view/timeline/TimeRange";

const DEFAULT_MAX_LENGTH = 100;

const schema = z
	.array(z.tuple([z.number(), z.number()]).transform(([start, end]) => new SpecificRange(start, end)))
	.optional();

export const TIME_RANGE_HISTORY = "time-range-history";

export interface TimeRangeHistoryInitOptions {
	/**
	 * @default []
	 */
	items?: ReadonlyArray<SpecificRange>;

	/**
	 * @default 100
	 */
	maxLength?: number;
}

export class TimeRangeHistory {
	public static init({
		items = [],
		maxLength = DEFAULT_MAX_LENGTH,
	}: TimeRangeHistoryInitOptions = {}): TimeRangeHistory {
		const uniqItems = uniqBy(items, (timeRange) =>
			// cannot use toUserFriendlyString because it does not format milliseconds
			[timeRange.start(), timeRange.end].filter(isPresent).map(getTime).join("|"),
		);

		return new TimeRangeHistory(take(uniqItems, maxLength), maxLength);
	}

	public static decode(json: string): TimeRangeHistory {
		const [error, items] = fromJSON(json, schema);

		if (error != null) {
			return TimeRangeHistory.init();
		}

		return TimeRangeHistory.init({ items });
	}

	private constructor(
		private readonly items: ReadonlyArray<SpecificRange>,
		private readonly maxLength: number,
	) {}

	/**
	 * Adds the time range to the history.
	 * Filters out the duplicates.
	 * Maintains maxLength size.
	 */
	public add(timeRange: SpecificRange): TimeRangeHistory {
		// if the timeRange is already present at the newest item - do not change anything
		if (first(this.items)?.isEqual(timeRange)) {
			return this;
		}

		const items = take(
			[
				timeRange,
				// if the history already has the timeRange - drop it
				...this.items.filter((item) => !item.isEqual(timeRange)),
			],
			this.maxLength,
		);

		return new TimeRangeHistory(items, this.maxLength);
	}

	/**
	 * Returns the current items where the first one is the newest.
	 */
	public getItems(): Array<SpecificRange> {
		return this.items as Array<SpecificRange>;
	}

	public encode(): unknown {
		return this.getItems().map((timeRange) => [timeRange.start(), timeRange.end ?? 0].map(getTime));
	}
}

import { TimeRangeHistoryContext } from "./_time-range-history-context";

export const useTimeRangeHistory = (): undefined | Signal<TimeRangeHistory> => useContext(TimeRangeHistoryContext);

export const useInitTimeRangeHistory = (timeRangeFromSearch: undefined | TimeRange): Signal<TimeRangeHistory> => {
	const timeRangeHistory = useMemo(
		() =>
			Signal(
				(monitor) => {
					const json = LocalStorage.getItem(monitor, TIME_RANGE_HISTORY);

					return TimeRangeHistory.decode(json ?? "");
				},
				(next) => {
					LocalStorage.setItem(TIME_RANGE_HISTORY, next.encode());
				},
			),
		[],
	);

	useEffect(() => {
		if (timeRangeFromSearch?.isSpecific()) {
			timeRangeHistory.write((current) => current.add(timeRangeFromSearch));
		}
	}, [timeRangeHistory, timeRangeFromSearch]);

	return timeRangeHistory;
};
